import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { t } from '../i18n.js';
import { ICOON } from './iconen.jsx';

/**
 * Beleid op de FMC (docs/besluiten/beleid-op-de-fmc.md): de platformbeleiden en toegangsbeleiden, met de
 * firewalls waaraan ze hangen; aanmaken en hernoemen. Verwijderen niet. Toewijzen gaat bij de firewall
 * (Syslog-standaard). Leest live van de FMC: alleen op verzoek.
 */
const SOORT = { platform: 'Platformbeleid', toegang: 'Toegangsbeleid' };
const ACTIE = { BLOCK: 'Block all traffic', TRUST: 'Trust all traffic', NETWORK_DISCOVERY: 'Network Discovery only' };

export default function BeleidFmc({ bron, mij }) {
  const magUitrollen = mij.permissions.includes('bronnen.uitrollen');
  const [vraag, zetVraag] = useState(false);
  const q = useQuery({ queryKey: ['fmc-beleid', bron.id], queryFn: () => api.fmcBeleid(bron.id), enabled: vraag, retry: false });
  const [nieuw, zetNieuw] = useState({ soort: 'toegang', naam: '', standaardactie: 'BLOCK' });
  const [hernoem, zetHernoem] = useState(null);
  const [melding, zetMelding] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const d = q.data?.data;

  const doe = async (fn, goed) => {
    if (bezig) return;
    zetMelding(null); zetBezig(true);
    try { await fn(); zetMelding({ goed: true, tekst: goed }); await q.refetch(); }
    catch (e) { zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) }); }
    finally { zetBezig(false); }
  };

  const tabel = (soort, lijst) => (
    <>
      <h3 style={{ margin: '14px 0 6px' }}>{t(SOORT[soort])}</h3>
      <table>
        <thead><tr><th>{t('Naam')}</th><th>{t('Hangt aan')}</th>{soort === 'toegang' && <th className="hide-sm">{t('Standaardactie')}</th>}{magUitrollen && <th />}</tr></thead>
        <tbody>{lijst.map((p) => (
          <tr key={p.id}>
            <td className="mono">{hernoem?.id === p.id
              ? <span style={{ display: 'flex', gap: 6 }}><input className="input mono" value={hernoem.naam} onChange={(e) => zetHernoem({ ...hernoem, naam: e.target.value })} aria-label={t('Nieuwe naam')} />
                <button type="button" className="btn primary" disabled={bezig || !hernoem.naam.trim() || hernoem.naam.trim() === p.name}
                  onClick={() => doe(() => api.hernoemFmcBeleid(bron.id, soort, p.id, hernoem.naam.trim()).then(() => zetHernoem(null)), t('Hernoemd.'))}>{t('Opslaan')}</button>
                <button type="button" className="btn" onClick={() => zetHernoem(null)}>{t('Annuleren')}</button></span>
              : p.name}</td>
            <td>{p.devices.length ? p.devices.map((x) => `${x.name}${x.via ? ` (${t('via {groep}', { groep: x.via })})` : ''}`).join(', ') : <span className="muted">{t('geen firewall')}</span>}</td>
            {soort === 'toegang' && <td className="mono muted hide-sm">{p.defaultAction ?? '—'}</td>}
            {magUitrollen && <td style={{ textAlign: 'right' }}>{hernoem?.id !== p.id && (
              <button type="button" className="icoonknop" title={t('Naam wijzigen')} aria-label={t('Naam wijzigen')}
                onClick={() => { zetMelding(null); zetHernoem({ id: p.id, naam: p.name }); }}>{ICOON.potlood}</button>)}</td>}
          </tr>
        ))}</tbody>
      </table>
    </>
  );

  return (
    <section className="card">
      <header><h2>{t('Beleid op de FMC')}</h2>
        <span className="meta"><button type="button" className="namelink" disabled={q.isFetching} onClick={() => (vraag ? q.refetch() : zetVraag(true))}>
          {q.isFetching ? t('Bezig met lezen van de FMC…') : vraag ? t('Opnieuw lezen') : t('Lezen van de FMC')}</button></span></header>
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 10 }}>{melding.tekst}</div>}
      {q.isError && <div className="melder rood">{t(q.error.message)}</div>}
      {d && !d.available && <p className="empty">{t(d.reason)}</p>}
      {d?.available && (
        <>
          {tabel('platform', d.platform)}
          {tabel('toegang', d.access)}
          {magUitrollen && (
            <form className="form" style={{ marginTop: 14 }} onSubmit={(e) => {
              e.preventDefault();
              doe(() => api.maakFmcBeleid(bron.id, { ...nieuw, naam: nieuw.naam.trim() }).then(() => zetNieuw((x) => ({ ...x, naam: '' }))), t('Aangemaakt, met de syslog-standaard erin.'));
            }}>
              <h3 style={{ margin: 0 }}>{t('Beleid aanmaken')}</h3>
              <div className="two">
                <label className="field"><span>{t('Soort')}</span>
                  <select value={nieuw.soort} onChange={(e) => zetNieuw((x) => ({ ...x, soort: e.target.value }))}>
                    <option value="toegang">{t('Toegangsbeleid')}</option><option value="platform">{t('Platformbeleid')}</option></select></label>
                <label className="field"><span>{t('Naam')}</span>
                  <input className="input mono" value={nieuw.naam} onChange={(e) => zetNieuw((x) => ({ ...x, naam: e.target.value }))} placeholder="ACL-KLANT-FW01" /></label>
              </div>
              {nieuw.soort === 'toegang' && <label className="field"><span>{t('Standaardactie')}</span>
                <select value={nieuw.standaardactie} onChange={(e) => zetNieuw((x) => ({ ...x, standaardactie: e.target.value }))}>
                  {d.defaultActions.map((a) => <option key={a} value={a}>{ACTIE[a] ?? a}</option>)}</select>
                <small>{t('Zonder regels doet de standaardactie alles: Block all traffic laat niets door.')}</small></label>}
              <div className="actions"><button className="btn primary" disabled={bezig || !nieuw.naam.trim()}>{bezig ? t('Bezig…') : t('Aanmaken')}</button></div>
              <p className="noot">{t('Een nieuw beleid hangt nergens aan. ADTS ZERO zet er meteen de syslog-standaard in (zonder de server: die hangt af van de firewall). Verwijderen kan in ADTS ZERO niet.')}</p>
            </form>
          )}
        </>
      )}
    </section>
  );
}
